import "./Reactive.css";
import { useDispatch, useSelector } from "react-redux";
import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useConsultantVisibleContext } from "../../features/consultant/screenContextAdapter";
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { useMeasurementGroups } from "../../hooks/useMeasurementGroups";
import { getInitialDatesFromStorageOrToday } from "../../helpers/measurement-secGroup";
import { useImageExport } from "../../hooks/useImageExport";
import { useDataFetching } from "../../helpers/useDataFetching";
import { autofillFromSingleCurrentDevice } from "../../helpers/consumptionAutoFromSingleDevice";
import MeasurementContext from "../../context/MeasurementContext";
import DragDropCompanies from "../../components/Measurement/DragDropCompanies";
import DragDropDevices from "../../components/Measurement/DragDropDevices";
import FrequencyComponent from "../../components/FilterFrequency/FilterFrequency";
import GroupSelector from "../../components/Measurement/GroupSelector";
import Chart1 from "../../components/Charts/Chart1";
import ChartControls from "../../components/Measurement/ChartControls";
import Chart2 from "../../components/Charts/Chart2";
import SendToDashboardModal from "../../components/Measurement/Consumption/SendToDashboardModal.js";
import MaximeterConfigModal from "../../components/Modals/MaximeterConfigModal";
import { useChartData } from "../../hooks/useChartData";
import {
  createWidget,
  getAllWidgets,
} from "../../redux/apiCalls/widgetCall/widgetCall";
import { generateID, paramTagToMeasureType } from "../../helpers/widgetsCharts";
import { Toast } from "../../helpers/alert";

const Reactive = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.currentUser);
  const userToken = user?.accessToken;
  const [graph, setGraph] = useState("Line");
  const graphRef = useRef(null);
  const currentDevices = useSelector((state) => state.devices.currentDevices);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [frec, setFrec] = useState("");
  const [firstSelect, setFirstSelect] = useState([]);
  const [devicesSelected, setDevicesSelected] = useState([]);
  const [dataReactiva, setDataReactiva] = useState([]);
  const [meterSelected, setMeterSelected] = useState("");
  useConsultantVisibleContext({ firstSelect, devicesSelected, startDate, endDate,
    selected_metric: meterSelected?.label || meterSelected?.tag, selected_unit: meterSelected?.unit, selected_chart: graph });
  const [isMeterLocked, setIsMeterLocked] = useState(false);
  const [openDropdown, setOpenDropdown] = useState({});
  const [showInput, setShowInput] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [availableBoards, setAvailableBoards] = useState([]);
  const exportNameRef = useRef(null);
  const { date } = useChartData(dataReactiva, meterSelected);

  // Obtener grupos disponibles basados en la compañía seleccionada
  const { allGroups, primary, setPrimary, second, setSecond } =
    useMeasurementGroups(dispatch, userToken, { firstSelect });

  useEffect(() => {
    const { startDate: initialStart, endDate: initialEnd } =
      getInitialDatesFromStorageOrToday();
    setStartDate(initialStart);
    setEndDate(initialEnd);
  }, []);

  const fetchStartDay = useMemo(() => {
    if (!startDate) return null;
    const x = new Date(startDate);
    x.setHours(0, 0, 0, 0);
    return x;
  }, [startDate]);

  const fetchEndDay = useMemo(() => {
    if (!endDate) return null;
    const x = new Date(endDate);
    x.setHours(23, 59, 59, 999);
    return x;
  }, [endDate]);

  const { captureScreen, exportToPDF, handleImageExport } = useImageExport(
    firstSelect,
    meterSelected,
    fetchStartDay,
    fetchEndDay,
  );

  useEffect(() => {
    if (!userToken) return;

    getAllWidgets(dispatch, userToken).then((widgetsByBoard) => {
      const boards = Object.entries(widgetsByBoard)
        .filter(([_, widgets]) =>
          widgets.some((w) => w.template === "custom" && !w.config),
        )
        .map(([boardName, widgets]) => {
          const placeholder = widgets.find(
            (w) => w.template === "custom" && !w.config,
          );
          const realWidgets = widgets.filter(
            (w) => w.template === "custom" && w.config,
          );

          return {
            name: boardName,
            displayName: placeholder?.name || boardName,
            widgets: realWidgets,
          };
        });

      setAvailableBoards(boards);
    });
  }, [dispatch, userToken]);

  // Fetching de datos con useDataFetching (igual que Consumption)
  useDataFetching(
    devicesSelected,
    dispatch,
    userToken,
    fetchStartDay,
    fetchEndDay,
    frec,
    setDataReactiva,
    meterSelected,
  );

  useEffect(() => {
    if (!startDate || !endDate) return;

    autofillFromSingleCurrentDevice({
      currentDevices,
      firstSelect,
      setFirstSelect,
      primary,
      setPrimary,
      secondary: second,
      setSecondary: setSecond,
      devicesSelected,
      setDevicesSelected,
      startDate,
      endDate,
      frec,
      meterSelected,
    });
  }, [
    currentDevices,
    startDate,
    endDate,
    frec,
    meterSelected,
    firstSelect,
    primary,
    second,
    devicesSelected,
    setPrimary,
    setSecond,
  ]);

  const handleValuesChange = (newFrec, newStartDate, newEndDate) => {
    setFrec(newFrec);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleGraphChange = (e) => setGraph(e.currentTarget.name);

  const handleExportClick = () => {
    setShowInput((prev) => !prev);
    setButtonVisible(false);
  };

  const handleSendToDashboard = async (selectedBoard, nameDisplay) => {
    const id_widget = generateID();

    const selected = availableBoards.find((b) => b.name === selectedBoard);
    const realWidgets = selected?.widgets || [];

    let maxY = 0;
    realWidgets.forEach((w) => {
      try {
        const cfg =
          typeof w.config === "string" ? JSON.parse(w.config) : w.config;
        const y = cfg?.y ?? 0;
        const h = cfg?.h ?? 1;
        if (y + h > maxY) maxY = y + h;
      } catch (e) {
        console.warn("Error al parsear config:", w);
      }
    });

    const config = JSON.stringify({
      x: 0,
      y: maxY,
      w: 4,
      h: 13,
      chartType: graph.toLowerCase(),
      unit: meterSelected?.unit || "N/A",
    });

    const payload = {
      id_widget,
      mac: devicesSelected.map((d) => d.mac).join(","),
      startDate: fetchStartDay?.toISOString?.() ?? startDate?.toISOString?.(),
      endDate: fetchEndDay?.toISOString?.() ?? endDate?.toISOString?.(),
      frec,
      config,
      measureType:
        paramTagToMeasureType[meterSelected?.tag] ||
        meterSelected?.tag?.toLowerCase(),
      chartType: graph.toLowerCase(),
      template: "custom",
      board: selectedBoard,
      name: nameDisplay,
      id_user: user?.id_user,
    };

    try {
      await createWidget(dispatch, payload, userToken);
      setModalOpen(false);
    } catch (err) {
      console.error("Error al crear widget:", err);
      Toast.fire({
        icon: "info",
        title: "Se produjó un error al enviar el gráfico. Intente nuevamente.",
      });
    }
  };

  const calculateResults = useMemo(() => {
    const values = {};
    return values;
  }, []);

  const isSendDisabled = useMemo(() => {
    return !(
      startDate &&
      endDate &&
      devicesSelected.length > 0 &&
      meterSelected?.tag
    );
  }, [startDate, endDate, devicesSelected, meterSelected]);

  // Fijar el meterSelected una vez seleccionado
  const handleSetMeterSelected = useCallback(
    (meter) => {
      if (!isMeterLocked && meter) {
        setMeterSelected(meter);
        setIsMeterLocked(true);
      }
    },
    [isMeterLocked],
  );

  const contextValue = useMemo(
    () => ({ meterSelected, setMeterSelected: handleSetMeterSelected }),
    [meterSelected, handleSetMeterSelected],
  );

  const deviceNameByMac = useMemo(() => {
    const m = {};
    (devicesSelected || []).forEach((d) => {
      const mac = String(d?.mac || "")
        .trim()
        .toUpperCase();
      if (!mac) return;

      const name = d?.data?.name?.trim() || d?.name?.trim() || mac;
      m[mac] = name;
    });
    return m;
  }, [devicesSelected]);

  const ChartComponent = useMemo(() => {
    if (!dataReactiva) return null;

    const props = {
      dataChart: dataReactiva,
      labels: date,
      measureType: meterSelected?.tag,
      unit: meterSelected?.unit,
      deviceNameByMac,
    };

    switch (graph) {
      case "Bar":
        return <Chart2 {...props} />;
      default:
        return <Chart1 {...props} />;
    }
  }, [
    graph,
    dataReactiva,
    date,
    meterSelected?.tag,
    meterSelected?.unit,
    deviceNameByMac,
  ]);

  return (
    <MeasurementContext.Provider value={contextValue}>
      <div className="container-reactive">
        <div className="page-content">
          <div className="cont-path-settings">
            <div className="cont-path">
              <p className="path path-cursor" onClick={() => navigate(`/`)}>
                <FormattedMessage id="Dashboard" defaultMessage="Dashboard" />
                {"> "}
              </p>
              <p className="path">
                <FormattedMessage
                  id="Reactive"
                  defaultMessage="Energía Reactiva"
                />
              </p>
            </div>
            {user?.privilege === "master" && (
              <div>
                <button
                  className="config-btn"
                  onClick={() => setConfigModalOpen(true)}
                >
                  Configuración
                </button>
              </div>
            )}
          </div>

          <div className="measurement-reactive">
            <DragDropCompanies
              setFirstSelect={setFirstSelect}
              firstSelect={firstSelect}
              maxSelections={1}
            />

            <GroupSelector
              allGroups={allGroups}
              primary={primary}
              setPrimary={setPrimary}
              second={second}
              setSecond={setSecond}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              firstSelect={firstSelect}
              filterPrimaryGroup={["Electric", "Confort", "Refrigeration"]}
            />

            <DragDropDevices
              setDevicesSelected={setDevicesSelected}
              firstSelect={firstSelect}
              primary={primary}
              second={second}
              prefillDevices={devicesSelected}
              showParameters={false}
              requiredMeterTags={["ENERGIA INDUCTIVA"]}
              lockMeter={isMeterLocked}
            />

            <FrequencyComponent
              onValuesChange={handleValuesChange}
              startDate={startDate}
              endDate={endDate}
              frec={frec}
            />

            <div className="graph" id="graph" ref={graphRef}>
              {ChartComponent}
            </div>

            <ChartControls
              onGraphChange={handleGraphChange}
              onCaptureScreen={captureScreen}
              onExportPDF={() => exportToPDF(graphRef)}
              showExportInput={showInput}
              onExportClick={handleExportClick}
              onExportSubmit={() =>
                handleImageExport(graphRef, calculateResults)
              }
              buttonVisible={buttonVisible}
              nameInputRef={exportNameRef}
              setModalOpen={setModalOpen}
              containerRef={graphRef}
            />

            <SendToDashboardModal
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
              onSubmit={handleSendToDashboard}
              boards={availableBoards}
              isSendDisabled={isSendDisabled}
            />
          </div>
        </div>
      </div>

      <MaximeterConfigModal
        isOpen={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
      />
    </MeasurementContext.Provider>
  );
};

export default Reactive;
